import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type ActiveModel,
  type FeatureGroupWeights,
  type ModelConfig,
  type ModelLabSplitPreview,
  type ModelRun,
  type TimeSplit,
} from "../lib/api";
import { useActiveModel } from "../lib/activeModel";
import { useAuth } from "../lib/auth";
import { useDisplayPreferences } from "../lib/displayPreferences";

type WeightGroupDef = {
  id: string;
  label: string;
  desc: string;
  detail: string;
  default: number;
  min: number;
  max: number;
};

type WeightClusterDef = {
  cluster: string;
  clusterDesc: string;
  clusterColor: string;
  groups: WeightGroupDef[];
};

// Mirrors the design mock (Model Lab.html) — more granular sections + educational copy.
const FEATURE_GROUPS: WeightClusterDef[] = [
  {
    cluster: "Contextual",
    clusterDesc: "Situational factors around the game itself",
    clusterColor: "#6366f1",
    groups: [
      {
        id: "home_venue",
        label: "Home / Venue",
        desc: "Home-court indicator and team-specific home/away tendencies.",
        detail:
          "Teams perform differently at home vs away across the WNBA. This group captures the typical home-court advantage and each team's historical deviation from the league mean at their own venue.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        // Backend canonical key is `rest_schedule` (legacy aliases: `rest`, `schedule`).
        id: "rest_schedule",
        label: "Rest & Schedule",
        desc: "Days of rest, back-to-backs, travel distance.",
        detail:
          "Schedule compression—back-to-backs and short rest—is one of the most reliable fatigue signals in basketball. This group captures rest differential between opponents and schedule congestion.",
        default: 1.8,
        min: 0.0,
        max: 3.0,
      },
    ],
  },
  {
    cluster: "Performance",
    clusterDesc: "What teams have actually been doing on the floor",
    clusterColor: "#22d3ee",
    groups: [
      {
        id: "team_strength",
        label: "Team Strength",
        desc: "Net rating, recent form (win%/streak), SOS, H2H, Elo-style ratings.",
        detail:
          "Rolling net point differential, opponent-adjusted efficiency, recent win-form, strength-of-schedule rolls, and head-to-head priors vs the opponent. Weighted toward recent games. This is typically the highest-signal group—how good a team actually is.",
        default: 0.7,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "pace_tempo",
        label: "Pace / Tempo",
        desc: "Possessions-per-game and tempo stability.",
        detail:
          "Pace affects both the spread and the total. High-tempo teams amplify variance; low-tempo teams produce tighter games. Useful when fade/follow systems care about totals.",
        default: 2.5,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "shooting",
        label: "Shooting",
        desc: "eFG%, TS%, 3P attempt share — rolling team priors.",
        detail:
          "Built from team box scores: effective FG% and true-shooting-style efficiency, plus 3PA share of attempts. All values are time-safe priors (prior games only) and can move quickly after shooting-luck swings — weight them like any efficiency layer.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "rebounding",
        label: "Rebounding",
        desc: "Offensive/defensive board volume + OREB share of team glass.",
        detail:
          "Uses offensive / defensive / total rebounds as rolling volume priors, plus offensive-rebound share of a team’s own rebounds (from OR & TOT). Second-chance generation and defensive rebounding stability are core spread drivers—especially in games with fewer possessions.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "ball_security",
        label: "Ball Security",
        desc: "Assists, turnovers, AST/TOV, TO per possession — rolling priors.",
        detail:
          "Covers per-game assist and turnover counts (as priors) plus derived ball-control ratios. High assist/turnover systems lower offensive variance; turnover spikes add noise to margin outcomes.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "fouls_ft",
        label: "Fouls & free throws",
        desc: "FT rate (FTA/FGA) as a pressure-on-rim / bonus proxy.",
        detail:
          "The primary signal here is free-throw rate (attempts per field-goal attempt) as a rolling prior—useful for modeling games that trend toward foul-heavy, late-clock endings.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "minutes_continuity",
        label: "Minutes / Continuity",
        desc: "Rotation stability, returning minutes %, injury proxies.",
        detail:
          "When a team's rotation is disrupted (new players, changed usage), performance is harder to predict. This group uses minutes-share stability as an availability proxy without requiring an injury feed.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
      {
        id: "pbp_leverage",
        label: "Clutch / PbP Leverage",
        desc: "Late-game patterns derived from play-by-play.",
        detail:
          "Rolling priors on clutch-time scoring, foul rates in close games, and fourth-quarter tendencies. All signals are time-safe—no same-game data leakage.",
        default: 1.0,
        min: 0.0,
        max: 3.0,
      },
    ],
  },
  {
    cluster: "Market",
    clusterDesc: "What the betting market is pricing in",
    clusterColor: "#f5a623",
    groups: [
      {
        id: "market",
        label: "Market Lines",
        desc: "Opening lines, moneyline-implied probabilities, line movement.",
        detail:
          "Market-implied win probabilities from opening lines carry aggregated information from sharp bettors and the book's own models. High weight here means you trust the market signal more than your performance features.",
        default: 1.3,
        min: 0.0,
        max: 3.0,
      },
    ],
  },
];

function normalizeWeightKeysForApi(
  raw: FeatureGroupWeights,
): FeatureGroupWeights {
  const any = raw as Record<string, unknown>;
  let out = raw;

  // Migration: mock UI used `rest_travel`; backend canonical key is `rest_schedule`.
  const restTravel = any.rest_travel;
  if (restTravel != null && raw.rest_schedule == null) {
    const { rest_travel: _rt, ...rest } = any;
    out = {
      ...(rest as FeatureGroupWeights),
      rest_schedule: restTravel as number,
    };
  } else if (restTravel != null && raw.rest_schedule != null) {
    const { rest_travel: _rt, ...rest } = any as Record<string, unknown>;
    out = rest as FeatureGroupWeights;
  }

  // Migration: UI used `clutch`; canonical key is `pbp_leverage` (matches API / saved configs).
  const o = out as Record<string, unknown>;
  const clutchVal = o.clutch;
  if (clutchVal != null && o.pbp_leverage == null) {
    const { clutch: _c, ...rest } = o;
    out = {
      ...(rest as FeatureGroupWeights),
      pbp_leverage: clutchVal as number,
    };
  } else if (clutchVal != null && o.pbp_leverage != null) {
    const { clutch: _c, ...rest } = o;
    out = rest as FeatureGroupWeights;
  }

  return out;
}

function defaultWeights(): FeatureGroupWeights {
  const all = FEATURE_GROUPS.flatMap((c) => c.groups);
  return Object.fromEntries(all.map((g) => [g.id, g.default]));
}

function baselineWeights(): FeatureGroupWeights {
  const all = FEATURE_GROUPS.flatMap((c) => c.groups);
  return Object.fromEntries(all.map((g) => [g.id, 1.0]));
}

function formatRunStatusLabel(status: ModelRun["status"]): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function todayISO(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

type SplitPresetKey = "default_30" | "last_60" | "last_90" | "custom";

function splitFromPreset(
  preview: ModelLabSplitPreview,
  preset: SplitPresetKey,
): TimeSplit | null {
  const max = preview.labeled_game_date_range.max;
  const min = preview.labeled_game_date_range.min;
  if (!max || !min) return null;

  const evalDays = preset === "last_60" ? 60 : preset === "last_90" ? 90 : 30;
  const maxD = new Date(`${max}T00:00:00Z`);
  const minD = new Date(`${min}T00:00:00Z`);
  const evalEnd = maxD;
  const evalStart = new Date(evalEnd);
  evalStart.setUTCDate(evalStart.getUTCDate() - (evalDays - 1));
  if (evalStart < minD) evalStart.setTime(minD.getTime());

  const trainEnd = new Date(evalStart);
  trainEnd.setUTCDate(trainEnd.getUTCDate() - 1);
  if (trainEnd < minD) trainEnd.setTime(minD.getTime());

  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return {
    train_end_date: iso(trainEnd),
    eval_start_date: iso(evalStart),
    eval_end_date: iso(evalEnd),
  };
}

function useLocalStorage<T>(key: string, initialValue: T | (() => T)) {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item !== null) {
        return JSON.parse(item) as T;
      }
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error as Error);
    }
    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  const setValueWrapped = useCallback(
    (valueToStore: T | ((val: T) => T)) => {
      setValue((prevValue) => {
        try {
          const valueToSave =
            valueToStore instanceof Function
              ? valueToStore(prevValue)
              : valueToStore;
          if (valueToSave === undefined) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(valueToSave));
          }
          return valueToSave;
        } catch (error) {
          console.warn(
            `Error setting localStorage key "${key}":`,
            error as Error,
          );
          return prevValue;
        }
      });
    },
    [key],
  );

  return [value, setValueWrapped] as const;
}

function formatFixed4(n: unknown): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(4) : "—";
}

function InfoTip({ label, content }: { label: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="ml-infotip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="ml-infotip-trigger"
        aria-label={`More info: ${label}`}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <span role="tooltip" className="ml-infotip-pop">
          <strong>{label}</strong>
          {content}
        </span>
      )}
    </span>
  );
}

function ModelRunMetricsView({ run }: { run: ModelRun }) {
  const m = run.metrics;
  const hasMetrics =
    !!m?.baseline &&
    !!m?.custom &&
    typeof m.baseline.log_loss === "number" &&
    typeof m.baseline.brier_score === "number" &&
    typeof m.baseline.n_samples === "number" &&
    typeof m.custom.log_loss === "number" &&
    typeof m.custom.brier_score === "number" &&
    typeof m.custom.n_samples === "number";

  if (!hasMetrics) {
    const note =
      run.status === "succeeded"
        ? "Metrics are unavailable for this run."
        : "Metrics appear after the run finishes.";
    return (
      <>
        <div
          className="model-lab-metric-cards"
          aria-label="Run metrics (pending)"
        >
          <div className="metric-card metric-card--baseline">
            <div className="metric-card-title">Baseline</div>
            <dl className="metric-card-dl">
              <div>
                <dt>Log loss</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>Brier</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>N</dt>
                <dd>—</dd>
              </div>
            </dl>
          </div>
          <div className="metric-card metric-card--custom">
            <div className="metric-card-title">My model</div>
            <dl className="metric-card-dl">
              <div>
                <dt>Log loss</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>Brier</dt>
                <dd>—</dd>
              </div>
              <div>
                <dt>N</dt>
                <dd>—</dd>
              </div>
            </dl>
          </div>
        </div>
        <p className="muted small" style={{ margin: "0.5rem 0 0" }}>
          {note}
        </p>
      </>
    );
  }
  return (
    <div className="model-lab-metric-cards">
      <div className="metric-card metric-card--baseline">
        <div className="metric-card-title">Baseline</div>
        <dl className="metric-card-dl">
          <div>
            <dt>Log loss</dt>
            <dd>{formatFixed4(m.baseline.log_loss)}</dd>
          </div>
          <div>
            <dt>Brier</dt>
            <dd>{formatFixed4(m.baseline.brier_score)}</dd>
          </div>
          <div>
            <dt>N</dt>
            <dd>{m.baseline.n_samples}</dd>
          </div>
        </dl>
      </div>
      <div className="metric-card metric-card--custom">
        <div className="metric-card-title">My model</div>
        <dl className="metric-card-dl">
          <div>
            <dt>Log loss</dt>
            <dd>{formatFixed4(m.custom.log_loss)}</dd>
          </div>
          <div>
            <dt>Brier</dt>
            <dd>{formatFixed4(m.custom.brier_score)}</dd>
          </div>
          <div>
            <dt>N</dt>
            <dd>{m.custom.n_samples}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

export function ModelLabPage() {
  const { user } = useAuth();
  const idToken = user?.idToken ?? null;
  const { modelLab, updateModelLab } = useDisplayPreferences();
  const { refresh: refreshActiveModel } = useActiveModel();
  const storageNs = useMemo(() => {
    const email = user?.email?.trim().toLowerCase();
    return email ? `modelLab:${email}` : "modelLab:anon";
  }, [user?.email]);
  const hydratedNsRef = useRef<string | null>(null);
  const hydratedKeyRef = useRef<string | null>(null);

  const [label, setLabel] = useLocalStorage(
    `${storageNs}.label`,
    "my-first-model",
  );
  const [weights, setWeights] = useLocalStorage<FeatureGroupWeights>(
    `${storageNs}.weights`,
    defaultWeights,
  );
  const [preset, setPreset] = useLocalStorage<SplitPresetKey>(
    `${storageNs}.splitPreset`,
    "default_30",
  );
  const [trainEnd, setTrainEnd] = useLocalStorage(`${storageNs}.trainEnd`, () =>
    todayISO(-30),
  );
  const [evalStart, setEvalStart] = useLocalStorage(
    `${storageNs}.evalStart`,
    () => todayISO(-29),
  );
  const [evalEnd, setEvalEnd] = useLocalStorage(`${storageNs}.evalEnd`, () =>
    todayISO(-1),
  );
  const [config, setConfig] = useLocalStorage<ModelConfig | null>(
    `${storageNs}.config`,
    null,
  );
  const [run, setRun] = useLocalStorage<ModelRun | null>(
    `${storageNs}.run`,
    null,
  );
  const [configsCatalog, setConfigsCatalog] = useState<ModelConfig[]>([]);
  const [runsCatalog, setRunsCatalog] = useState<ModelRun[]>([]);
  const [activeModel, setActiveModel] = useState<ActiveModel | null>(null);
  const [activateMessage, setActivateMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    "config" | "run" | "activate" | "hydrate" | "load_run" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [splitPreview, setSplitPreview] = useState<ModelLabSplitPreview | null>(
    null,
  );
  const [splitPreviewBusy, setSplitPreviewBusy] = useState(false);
  /** Avoid flashing "Checking split…" on fast or aborted preview requests. */
  const [splitCheckingVisible, setSplitCheckingVisible] = useState(false);
  const [configStartMode, setConfigStartMode] = useState<"saved" | "fresh">(
    "fresh",
  );
  const [baselineSeedWeights, setBaselineSeedWeights] =
    useState<FeatureGroupWeights | null>(null);
  const baselineSeedWeightsVisible = idToken ? baselineSeedWeights : null;

  const splitValid = useMemo(() => {
    if (preset === "custom")
      return trainEnd < evalStart && evalStart <= evalEnd;
    return splitPreview?.ok === true;
  }, [preset, trainEnd, evalStart, evalEnd, splitPreview]);

  const configWeightsMissing = useMemo(() => {
    if (!config) return false;
    return Object.keys(config.feature_group_weights).length === 0;
  }, [config]);

  const activeRunSummary = useMemo(() => {
    if (!activeModel?.run_id) return null;
    const r = runsCatalog.find((x) => x.run_id === activeModel.run_id);
    const cfg = configsCatalog.find((c) => c.config_id === r?.config_id);
    const cfgTitle = (
      cfg?.label ??
      cfg?.display_name ??
      r?.config_id ??
      ""
    ).trim();
    return { cfgTitle };
  }, [activeModel, runsCatalog, configsCatalog]);

  const configTitleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of configsCatalog) {
      const title = (c.label ?? c.display_name ?? "").trim();
      if (c.config_id && title) m.set(c.config_id, title);
    }
    return m;
  }, [configsCatalog]);

  const runsForCurrentConfig = useMemo(() => {
    if (!config?.config_id) return [];
    return runsCatalog
      .filter((r) => r.config_id === config.config_id)
      .slice()
      .sort((a, b) => {
        const ta = (a as { created_at?: string }).created_at ?? "";
        const tb = (b as { created_at?: string }).created_at ?? "";
        return tb.localeCompare(ta);
      });
  }, [config, runsCatalog]);

  const latestRunByConfigId = useMemo(() => {
    const m = new Map<string, ModelRun>();
    for (const r of runsCatalog) {
      const prev = m.get(r.config_id);
      if (!prev || r.created_at > prev.created_at) {
        m.set(r.config_id, r);
      }
    }
    return m;
  }, [runsCatalog]);

  useEffect(() => {
    if (!run || !config?.config_id) return;
    if (run.config_id !== config.config_id) setRun(null);
  }, [run, config?.config_id, setRun]);

  useEffect(() => {
    hydratedNsRef.current = null;
  }, [storageNs]);

  // Migrate any persisted local keys to canonical backend keys.
  useEffect(() => {
    setWeights((w) => normalizeWeightKeysForApi(w));
  }, [storageNs, setWeights]);

  // Hydrate server-side Model Lab state for this authenticated user.
  useEffect(() => {
    if (!idToken) return;
    // Guard: only hydrate once per (storage namespace + token).
    // This prevents state updates inside hydration from retriggering a fetch loop.
    const hydrateKey = `${storageNs}::${idToken}`;
    if (hydratedKeyRef.current === hydrateKey) return;
    hydratedKeyRef.current = hydrateKey;
    if (hydratedNsRef.current === storageNs) return;
    const ctrl = new AbortController();
    void (async () => {
      setBusy("hydrate");
      setError(null);
      try {
        const [configs, runs] = await Promise.all([
          api.listModelConfigs({ idToken, signal: ctrl.signal, limit: 50 }),
          api.listModelRuns({ idToken, signal: ctrl.signal, limit: 50 }),
        ]);
        setConfigsCatalog(configs);
        setRunsCatalog(runs);

        const preferredConfig =
          (config?.config_id
            ? configs.find((c) => c.config_id === config.config_id)
            : null) ??
          configs[0] ??
          null;

        if (preferredConfig) {
          setConfig(preferredConfig);
          setConfigStartMode("saved");
          const nextLabel =
            preferredConfig.label ?? preferredConfig.display_name;
          if (nextLabel) setLabel(nextLabel);
          setWeights({
            ...defaultWeights(),
            ...preferredConfig.feature_group_weights,
          });
        } else {
          setConfig(null);
          setRun(null);
          setConfigStartMode("fresh");
        }

        const pollable = runs.filter(
          (r) => r.status === "pending" || r.status === "running",
        );
        const terminal = runs.filter(
          (r) => r.status !== "pending" && r.status !== "running",
        );
        const preferredRun =
          (run?.run_id ? runs.find((r) => r.run_id === run.run_id) : null) ??
          pollable[0] ??
          terminal.find(
            (r) => r.config_id === (preferredConfig?.config_id ?? ""),
          ) ??
          terminal[0] ??
          null;

        if (preferredRun) {
          // Always refresh from the canonical GET endpoint so metrics/embeddings match server.
          const latestRun = await api.getModelRun(preferredRun.run_id, {
            idToken,
            signal: ctrl.signal,
          });
          setRun(latestRun);
        } else {
          setRun(null);
        }

        const active = await api.getActiveModel({
          idToken,
          signal: ctrl.signal,
        });
        setActiveModel(active);

        hydratedNsRef.current = storageNs;
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError((err as Error).message);
        }
      } finally {
        setBusy((prev) => (prev === "hydrate" ? null : prev));
      }
    })();
    return () => ctrl.abort();
  }, [
    idToken,
    storageNs,
    config?.config_id,
    run?.run_id,
    setConfig,
    setLabel,
    setRun,
    setWeights,
  ]);

  // Fetch baseline metadata so "Start from baseline" seeds from the baseline model's
  // feature-group weights when available.
  useEffect(() => {
    if (!idToken) {
      return;
    }
    void api
      .getSlate({ idToken, all: true })
      .then((res) => {
        const info = res.baseline?.info;
        const fgw = (info as Record<string, unknown> | null | undefined)
          ?.feature_group_weights;
        if (!fgw || typeof fgw !== "object") {
          setBaselineSeedWeights(null);
          return;
        }
        const weights: FeatureGroupWeights = {};
        for (const [k, v] of Object.entries(fgw as Record<string, unknown>)) {
          const n =
            typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
          if (Number.isFinite(n)) weights[k] = n;
        }
        setBaselineSeedWeights(Object.keys(weights).length ? weights : null);
      })
      .catch(() => setBaselineSeedWeights(null));
  }, [idToken]);

  const pollRunId = run?.run_id;
  const pollRunStatus = run?.status;

  useEffect(() => {
    if (
      !idToken ||
      !pollRunId ||
      !pollRunStatus ||
      !["pending", "running"].includes(pollRunStatus)
    ) {
      return;
    }
    const runId = pollRunId;
    const timer = setInterval(async () => {
      try {
        const latestRun = await api.getModelRun(runId, {
          idToken,
        });
        setRun(latestRun);
      } catch (err) {
        console.error("Failed to poll run status", err as Error);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [idToken, pollRunId, pollRunStatus, setRun]);

  useEffect(() => {
    if (!splitPreviewBusy) {
      queueMicrotask(() => setSplitCheckingVisible(false));
      return;
    }
    const t = window.setTimeout(() => setSplitCheckingVisible(true), 450);
    return () => window.clearTimeout(t);
  }, [splitPreviewBusy]);

  // Preview split: drives the UX (counts + guardrails) and lets presets be date-free.
  useEffect(() => {
    const idToken = user?.idToken;
    const configId = config?.config_id;
    if (!idToken || !configId) {
      queueMicrotask(() => {
        setSplitPreview(null);
        setSplitPreviewBusy(false);
      });
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      void (async () => {
        setSplitPreviewBusy(true);
        setError(null);
        try {
          if (preset === "custom") {
            const res = await api.previewModelLabSplit(
              {
                split: {
                  train_end_date: trainEnd,
                  eval_start_date: evalStart,
                  eval_end_date: evalEnd,
                },
              },
              { idToken, signal: ctrl.signal },
            );
            if (cancelled) return;
            setSplitPreview(res.preview);
          } else {
            const base = await api.previewModelLabSplit(
              {},
              { idToken, signal: ctrl.signal },
            );
            if (cancelled) return;
            const derived = splitFromPreset(base.preview, preset);
            if (!derived) {
              if (!cancelled) setSplitPreview(base.preview);
              return;
            }
            const res = await api.previewModelLabSplit(
              { split: derived },
              { idToken, signal: ctrl.signal },
            );
            if (cancelled) return;
            setSplitPreview(res.preview);
          }
        } catch (err) {
          if (cancelled || (err as Error).name === "AbortError") return;
          setSplitPreview(null);
          setError((err as Error).message);
        } finally {
          if (!cancelled) setSplitPreviewBusy(false);
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearTimeout(t);
      setSplitPreviewBusy(false);
    };
  }, [user?.idToken, config?.config_id, preset, trainEnd, evalStart, evalEnd]);

  const setWeight = useCallback(
    (key: string, v: number) => {
      setWeights((prev) => ({ ...prev, [key]: v }));
    },
    [setWeights],
  );

  const onSelectSavedConfig = useCallback(
    async (configId: string) => {
      if (!user) return;
      const selected =
        configsCatalog.find((c) => c.config_id === configId) ?? null;
      if (!selected) {
        setConfig(null);
        return;
      }
      setConfig(selected);
      setConfigStartMode("saved");
      const nextLabel = selected.label ?? selected.display_name;
      if (nextLabel) setLabel(nextLabel);
      setWeights({ ...defaultWeights(), ...selected.feature_group_weights });
      setRun(null);
    },
    [user, configsCatalog, setConfig, setLabel, setWeights, setRun],
  );

  const onStartFreshConfig = useCallback(() => {
    setConfigStartMode("fresh");
    setConfig(null);
    setRun(null);
    if (baselineSeedWeightsVisible) {
      setWeights({ ...baselineWeights(), ...baselineSeedWeightsVisible });
    } else {
      setWeights(baselineWeights());
    }
  }, [baselineSeedWeightsVisible, setConfig, setRun, setWeights]);

  const onSelectSavedRun = useCallback(
    async (runId: string) => {
      if (!user) return;
      if (!runId) {
        setRun(null);
        return;
      }
      setBusy("load_run");
      setError(null);
      try {
        const latestRun = await api.getModelRun(runId, {
          idToken: user.idToken,
        });
        setRun(latestRun);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [user, setRun],
  );

  const createConfig = useCallback(async () => {
    if (!user) return;
    setBusy("config");
    setError(null);
    try {
      const weightsForApi = normalizeWeightKeysForApi(weights);
      const c = await api.createModelConfig(
        { label, feature_group_weights: weightsForApi },
        { idToken: user.idToken },
      );
      setConfig(c);
      setRun(null);
      const refreshed = await api.listModelConfigs({
        idToken: user.idToken,
        limit: 50,
      });
      setConfigsCatalog(refreshed);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, label, weights, setConfig, setRun]);

  const submitRun = useCallback(async () => {
    if (!user || !config) return;
    if (!splitValid) {
      setError("Split is not valid for available labeled games.");
      return;
    }
    setBusy("run");
    setError(null);

    if (!config.config_id) {
      setError("Config is missing config_id.");
      setBusy(null);
      return;
    }

    try {
      const split =
        preset === "custom"
          ? {
              train_end_date: trainEnd,
              eval_start_date: evalStart,
              eval_end_date: evalEnd,
            }
          : undefined; // Let the backend default based on labeled games.
      const r = await api.createModelRun(
        {
          config_id: config.config_id,
          split,
        },
        { idToken: user.idToken },
      );
      setRun(r);
      const refreshedRuns = await api.listModelRuns({
        idToken: user.idToken,
        limit: 50,
      });
      setRunsCatalog(refreshedRuns);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, config, splitValid, preset, trainEnd, evalStart, evalEnd, setRun]);

  const activate = useCallback(async () => {
    if (!user || !run) return;
    setBusy("activate");
    setError(null);
    setActivateMessage(null);
    try {
      const active = await api.setActiveModel(
        { run_id: run.run_id },
        { idToken: user.idToken },
      );
      setActiveModel(active);
      refreshActiveModel();
      setActivateMessage(
        active.run_id
          ? `Active model updated. Slate requests will use run ${active.run_id} (reload Predictions to see changes).`
          : "Active model cleared.",
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, run, refreshActiveModel]);

  const labStep = useMemo(() => {
    if (!config) return 1;
    if (!run) return 2;
    if (run.status === "succeeded") {
      return activeModel?.run_id === run.run_id ? 4 : 3;
    }
    if (run.status === "failed") return 3;
    return 2;
  }, [activeModel?.run_id, config, run]);

  if (!user) {
    return (
      <section className="page">
        <h1>Model Lab</h1>
        <p className="muted">
          Sign in to build a custom WNBA prediction model. You&apos;ll set
          feature-group weights, pick a time split, and evaluate your model
          against the baseline on held-out future games.
        </p>
        <div className="card legal-callout" style={{ marginTop: "1rem" }}>
          <h2 style={{ marginTop: 0 }}>Responsible gaming</h2>
          <p className="muted" style={{ marginBottom: "0.5rem" }}>
            <strong>DISCLAIMER:</strong> This site is 100% for entertainment
            purposes only and does not involve real money betting or prizes.
          </p>
          <p className="muted small" style={{ marginTop: 0 }}>
            Sports betting is regulated and permitted only in select U.S.
            states. This content is intended solely for individuals of legal
            betting age (21+). Availability varies by location. No guarantees of
            outcomes are made. Please check your local laws and gamble
            responsibly.
          </p>
          <p className="muted small" style={{ marginBottom: 0 }}>
            Gambling problem? Call <strong>1-800-GAMBLER</strong>. You must be{" "}
            <strong>18+</strong> to use this site.
          </p>
          <p
            className="muted small"
            style={{ marginTop: "0.75rem", marginBottom: 0 }}
          >
            <Link to="/responsible-gaming">Responsible gaming</Link> ·{" "}
            <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="page model-lab-page">
      <header className="page-header model-lab-page-header">
        <h1>Model Lab</h1>
        <p className="muted">
          Weight the feature groups you trust more. Training and evaluation
          happen on strictly ordered time splits — no peeking at the eval
          window.
        </p>
      </header>

      <div className="card model-lab-display-toolbar">
        <span className="model-lab-toolbar-title">Display</span>
        <label className="slate-checkbox">
          <input
            type="checkbox"
            checked={modelLab.compact_sliders}
            onChange={(e) =>
              updateModelLab({ compact_sliders: e.target.checked })
            }
          />
          <span>Compact sliders</span>
        </label>
      </div>

      <div className="model-lab-stepper" aria-label="Model Lab progress">
        {[
          { n: 1, label: "Configure" },
          { n: 2, label: "Train" },
          { n: 3, label: "Review" },
          { n: 4, label: "Activate" },
        ].map(({ n, label }) => (
          <div
            key={n}
            className={`model-lab-step${labStep === n ? " model-lab-step--active" : ""}${
              labStep > n ? " model-lab-step--done" : ""
            }`}
          >
            <span className="model-lab-step-dot">{labStep > n ? "✓" : n}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className="model-lab-layout">
        <div className="model-lab-col model-lab-col--main">
          <div className="card">
            <h2>Feature Weights</h2>
            <p className="muted small">
              These sliders control how much each signal group influences
              training. Changes apply to the config you name and save in Step 1.
            </p>
            <div
              className={`weights-grid${modelLab.compact_sliders ? " weights-grid--compact" : ""}`}
            >
              {FEATURE_GROUPS.map((cluster) => (
                <section key={cluster.cluster} className="ml-weight-cluster">
                  <header className="ml-weight-cluster-head">
                    <span
                      className="ml-weight-cluster-dot"
                      aria-hidden
                      style={{ background: cluster.clusterColor }}
                    />
                    <div>
                      <div className="ml-weight-cluster-title">
                        {cluster.cluster}
                      </div>
                      <div className="muted small">{cluster.clusterDesc}</div>
                    </div>
                  </header>
                  <div className="ml-weight-cluster-body">
                    {cluster.groups.map((g) => (
                      <label key={g.id} className="weight-row">
                        <div>
                          <div className="ml-weight-row-title">
                            <strong>{g.label}</strong>
                            <InfoTip label={g.label} content={g.detail} />
                          </div>
                          <div className="muted small">{g.desc}</div>
                        </div>
                        <div className="weight-control">
                          <input
                            type="range"
                            min={g.min}
                            max={g.max}
                            step={0.1}
                            value={weights[g.id] ?? g.default}
                            onChange={(e) =>
                              setWeight(g.id, Number(e.target.value))
                            }
                          />
                          <span className="weight-value">
                            {(weights[g.id] ?? g.default).toFixed(1)}×
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>

        <div className="model-lab-col model-lab-col--side">
          <div className="card">
            <h2>Active model</h2>
            {activeModel?.run_id ? (
              <p className="muted small">
                Run <code>{activeModel.run_id}</code>
                {activeRunSummary?.cfgTitle ? (
                  <> · config {activeRunSummary.cfgTitle}</>
                ) : null}{" "}
                · updated <code>{activeModel.updated_at}</code>
              </p>
            ) : (
              <p className="muted small">
                No personal model is active yet. Predictions shows league
                baseline only until you activate a successful run.
              </p>
            )}
          </div>

          <div className="card model-lab-config-card">
            <h2>1. Configure</h2>
            <div>
              <span className="model-lab-subhead">Starting point</span>
              <div
                className="model-lab-start-options"
                role="group"
                aria-label="Config starting point"
              >
                <button
                  type="button"
                  className={`model-lab-start-option${configStartMode === "saved" ? " is-active" : ""}`}
                  disabled={configsCatalog.length === 0 || busy !== null}
                  onClick={() => setConfigStartMode("saved")}
                >
                  <span aria-hidden>📂</span>
                  <span>Load a saved config</span>
                </button>
                <button
                  type="button"
                  className={`model-lab-start-option${configStartMode === "fresh" ? " is-active" : ""}`}
                  disabled={busy !== null}
                  onClick={onStartFreshConfig}
                >
                  <span aria-hidden>✨</span>
                  <span>Start from baseline</span>
                </button>
              </div>
            </div>

            {configsCatalog.length > 0 && configStartMode === "saved" && (
              <label className="field">
                <span>Saved config</span>
                <select
                  value={config?.config_id ?? ""}
                  onChange={(e) => void onSelectSavedConfig(e.target.value)}
                  disabled={busy !== null}
                >
                  <option value="">Choose a saved config</option>
                  {configsCatalog.map((c) => {
                    const title =
                      (c.label ?? c.display_name ?? "Untitled").trim() ||
                      "Untitled";
                    const latest = latestRunByConfigId.get(c.config_id);
                    const statusLabel = latest
                      ? formatRunStatusLabel(latest.status)
                      : "No runs";
                    return (
                      <option key={c.config_id} value={c.config_id}>
                        {title} · {statusLabel} · {c.config_id.slice(0, 8)}…
                      </option>
                    );
                  })}
                </select>
                <p className="muted small">
                  Loads saved feature weights into the sidebar sliders. Adjust
                  them to create your new variant.
                </p>
              </label>
            )}
            {configWeightsMissing && (
              <p className="muted small">
                This saved config has no stored feature weights. Adjust sliders
                and click &quot;Save config&quot; to repair it, then train
                again.
              </p>
            )}
            {configStartMode === "fresh" && (
              <div className="model-lab-start-note">
                All weights reset to baseline defaults. Use the sidebar sliders
                to build your variant.
              </div>
            )}
            {configStartMode === "fresh" && baselineSeedWeightsVisible && (
              <div
                className="model-lab-start-note"
                style={{ marginTop: "-0.5rem" }}
              >
                <div className="muted small">
                  Seeded from baseline feature-group weights (best-in-class
                  league model).
                </div>
              </div>
            )}
            <label className="field">
              <span>Config name</span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="my-first-model"
              />
            </label>
            <div className="model-lab-train-link">
              <strong>
                &quot;{label || "untitled"}&quot; will be trained &amp;
                evaluated
              </strong>
              <span>
                The feature weights in the sidebar are saved with this config.
                Save it here before moving on to training.
              </span>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy !== null}
              onClick={createConfig}
            >
              {busy === "config" ? "Saving…" : "Save config & continue"}
            </button>
            {config && (
              <p className="muted small">
                Config id: <code>{config.config_id}</code>
              </p>
            )}
          </div>

          <div className="card" aria-disabled={!config}>
            <h2>2. Train &amp; evaluate</h2>
            {runsForCurrentConfig.length > 0 && (
              <div
                className="model-lab-run-cards"
                role="list"
                aria-label="Runs for this config"
              >
                {runsForCurrentConfig.slice(0, 12).map((r) => {
                  const selected = run?.run_id === r.run_id;
                  return (
                    <button
                      key={r.run_id}
                      type="button"
                      role="listitem"
                      className={`model-lab-run-card${selected ? " is-active" : ""}`}
                      aria-pressed={selected}
                      onClick={() => void onSelectSavedRun(r.run_id)}
                      title={`Run ${r.run_id}`}
                    >
                      <span
                        className={`model-lab-run-card-status model-lab-run-card-status--${r.status}`}
                      >
                        {r.status}
                      </span>
                      <span className="model-lab-run-card-title">
                        {(configTitleById.get(r.config_id) ?? "").trim() ||
                          `Config ${r.config_id.slice(0, 8)}…`}
                      </span>
                      <span className="muted small">
                        {(r as { created_at?: string }).created_at?.slice(
                          0,
                          10,
                        ) ?? "—"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="model-lab-eval-preset">
              <div className="model-lab-eval-preset-head">
                <span className="model-lab-eval-preset-label">
                  Evaluation preset
                </span>
                <InfoTip
                  label="Evaluation preset"
                  content="The training cutoff determines how many games are used to train vs evaluate your model. Earlier cutoffs give more eval games (better statistical stability) but older training data. All splits are strictly time-ordered — no peeking."
                />
              </div>
              <select
                value={preset}
                onChange={(e) => setPreset(e.target.value as SplitPresetKey)}
                disabled={busy !== null}
              >
                <option value="default_30">
                  Default (last 30 labeled days)
                </option>
                <option value="last_60">Last 60 labeled days</option>
                <option value="last_90">Last 90 labeled days</option>
                <option value="custom">Advanced: pick dates</option>
              </select>
            </div>

            {preset === "custom" && (
              <div className="split-grid">
                <label className="field">
                  <span>Train end (inclusive)</span>
                  <input
                    type="date"
                    value={trainEnd}
                    onChange={(e) => setTrainEnd(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Eval start</span>
                  <input
                    type="date"
                    value={evalStart}
                    onChange={(e) => setEvalStart(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Eval end</span>
                  <input
                    type="date"
                    value={evalEnd}
                    onChange={(e) => setEvalEnd(e.target.value)}
                  />
                </label>
              </div>
            )}

            {splitCheckingVisible && splitPreviewBusy && (
              <p className="muted small">Checking split…</p>
            )}
            {splitPreview && (
              <>
                <div
                  className="model-lab-data-summary"
                  aria-label="Training and evaluation split"
                >
                  <div>
                    <div className="model-lab-data-summary-k">Train games</div>
                    <div className="model-lab-data-summary-v">
                      {splitPreview.counts.n_train}
                    </div>
                    <div className="model-lab-data-summary-sub">
                      {splitPreview.labeled_game_date_range.min ?? "?"} →{" "}
                      {splitPreview.split.train_end_date}
                    </div>
                  </div>
                  <div>
                    <div className="model-lab-data-summary-k">Eval games</div>
                    <div className="model-lab-data-summary-v model-lab-data-summary-v--accent">
                      {splitPreview.counts.n_eval}
                    </div>
                    <div className="model-lab-data-summary-sub">
                      {splitPreview.split.eval_start_date} →{" "}
                      {splitPreview.split.eval_end_date}
                    </div>
                  </div>
                  <div>
                    <div className="model-lab-data-summary-k">Data range</div>
                    <div className="model-lab-data-summary-v2">
                      {(() => {
                        const min =
                          splitPreview.labeled_game_date_range.min ?? "";
                        const max =
                          splitPreview.labeled_game_date_range.max ?? "";
                        const y1 = min.slice(0, 4);
                        const y2 = max.slice(0, 4);
                        return y1 && y2 ? `${y1}–${y2}` : "—";
                      })()}
                    </div>
                    <div className="model-lab-data-summary-sub">
                      WNBA labeled games
                    </div>
                  </div>
                </div>
                {splitPreview.warnings?.length > 0 && (
                  <p className="muted small">
                    {splitPreview.warnings.join(" ")}
                  </p>
                )}
                {splitPreview.errors?.length > 0 && (
                  <p className="error small">{splitPreview.errors.join(" ")}</p>
                )}
              </>
            )}

            <button
              type="button"
              className={`btn btn-primary${busy === "run" || run?.status === "pending" || run?.status === "running" ? " btn-primary--training" : ""}`}
              disabled={
                !config ||
                !splitValid ||
                busy !== null ||
                run?.status === "pending" ||
                run?.status === "running"
              }
              onClick={submitRun}
            >
              {(busy === "run" ||
                run?.status === "pending" ||
                run?.status === "running") && (
                <span className="btn-spinner" aria-hidden />
              )}
              {busy === "run"
                ? "Submitting…"
                : run?.status === "pending" || run?.status === "running"
                  ? "Training…"
                  : "Train & evaluate"}
            </button>

            {(busy === "run" ||
              run?.status === "pending" ||
              run?.status === "running") && (
              <div className="model-lab-train-progress" aria-hidden>
                <div className="model-lab-train-progress-bar" />
              </div>
            )}

            <div className="model-lab-trust-box">
              <strong>My model</strong> — your trained model variant (feature
              weights) evaluated on held-out games. Training and evaluation
              happen on strictly ordered time splits — no peeking at the eval
              window.
            </div>
            {run && (
              <div className="run-summary">
                <p>
                  Run <code>{run.run_id}</code>: <strong>{run.status}</strong>
                </p>
                <p className="muted small">
                  Log loss is a standard scoring rule for probabilities: it
                  penalizes confident wrong predictions more than cautious ones.
                  Lower is better. Brier score measures how close your predicted
                  win probabilities are to the true 0/1 outcomes (calibration +
                  sharpness for a binary event). Lower is better. N is the
                  number of held-out games in the eval window.
                </p>
                <ModelRunMetricsView run={run} />
              </div>
            )}
          </div>

          <div
            className="card"
            aria-disabled={!run || run.status !== "succeeded"}
          >
            <h2>4. Activate</h2>
            <p className="muted small">
              Setting this run as active makes its predictions show up next to
              the baseline on the slate. You can always switch back.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!run || run.status !== "succeeded" || busy !== null}
              onClick={activate}
            >
              {busy === "activate" ? "Activating…" : "Activate this run"}
            </button>
            {activateMessage && (
              <p className="muted small">{activateMessage}</p>
            )}
          </div>
        </div>
      </div>

      <section className="card model-lab-roadmap" aria-label="Product roadmap">
        <h2>Roadmap</h2>
        <p className="muted small">
          Post-launch product modules — placeholders only in this build.
        </p>
        <div className="model-lab-locked-row">
          {[
            "Feature studio",
            "Slice metrics",
            "Calibration charts",
            "Scenario scoring",
            "Semantic backtest",
          ].map((title) => (
            <div key={title} className="locked-tab" title="Coming after launch">
              <span className="lock-icon" aria-hidden>
                🔒
              </span>
              <span>{title}</span>
            </div>
          ))}
        </div>
      </section>

      {error && <p className="error">{error}</p>}
    </section>
  );
}
